import {
    DataTable,
    DateField,
    EditButton,
    ReferenceField,
    ReferenceManyField,
    Show,
    TextField,
} from '@/components/admin';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, ExternalLink, Eye, EyeOff, FileText, Landmark, Truck } from 'lucide-react';
import { useGetList, useRecordContext } from 'ra-core';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useConfigurationContext } from '../../root/ConfigurationContext';
import { TopToolbar } from '../../layout/TopToolbar';
import { AddressBlock } from '../shared/AddressBlock';
import { DocumentPDFButton } from '../shared/DocumentPDFButton';
import { StatusBadge } from '../shared/StatusBadge';
import { WatermarkOverlay } from '../shared/WatermarkOverlay';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Arial:wght@400;700&display=swap');

  .po-wrap {
    font-family: Arial, sans-serif;
    font-size: 13px;
    color: #000;
    background: #e8eaf0;
    min-height: 100vh;
    padding: 40px 20px;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .po-sheet {
    background: #fff;
    width: 210mm;
    height: 297mm;
    min-height: 297mm;
    display: flex;
    flex-direction: column;
    margin: 0 auto 30px auto;
    padding: 10mm;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
    position: relative;
    box-sizing: border-box;
  }

  .po-sheet-inner {
    border: 1.5px solid #000;
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 16px 24px;
    box-sizing: border-box;
    position: relative;
    height: 100%;
    overflow: hidden;
  }

  /* ── HEADER ── */
  .po-header-bar {
    display: grid;
    grid-template-columns: 200px 1fr 240px;
    align-items: stretch;
    margin-top: -16px;
    margin-left: -24px;
    margin-right: -24px;
    margin-bottom: 0;
    border-bottom: 1.5px solid #000;
  }
  .po-header-logo {
    padding: 10px 12px;
    border-right: 1.5px solid #000;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .po-header-logo img { width: 160px; height: auto; display: block; }
  .po-header-title {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 10px;
  }
  .po-header-meta {
    border-left: 1.5px solid #000;
    padding: 0;
    font-size: 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .po-meta-row {
    display: grid;
    grid-template-columns: 90px 1fr;
    border-bottom: 1.5px solid #000;
    font-size: 11px;
    align-items: stretch;
    flex: 1;
  }
  .po-meta-row:last-child {
    border-bottom: none;
  }
  .po-meta-lbl {
    font-weight: 700;
    white-space: nowrap;
    text-transform: uppercase;
    color: #555;
    padding: 5px 8px;
    border-right: 1.5px solid #000;
    display: flex;
    align-items: center;
  }
  .po-meta-val {
    font-weight: 700;
    color: #000;
    padding: 5px 8px;
    display: flex;
    align-items: center;
  }

  /* ── FROM / TO / SHIP TO GRID ── */
  .so-from-to-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-left: -24px;
    margin-right: -24px;
    border-bottom: 1.5px solid #000;
  }
  .so-from-to-grid .address-block {
    padding: 0 !important;
    margin: 0 !important;
  }
  .so-from-to-grid .address-block:first-child {
    border-right: 1.5px solid #000 !important;
  }
  .so-from-to-grid .address-block h4,
  .so-ship-to-bar .address-block h4 {
    margin: 0 !important;
    padding: 6px 12px !important;
    background: #f1f5f9 !important;
    border-bottom: 1.5px solid #000 !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.05em !important;
    color: #000 !important;
  }
  .so-from-to-grid .address-block > div,
  .so-ship-to-bar .address-block > div {
    padding: 10px 12px !important;
  }

  .so-ship-to-bar {
    margin-left: -24px;
    margin-right: -24px;
    border-bottom: 1.5px solid #000;
  }
  .so-ship-to-bar .address-block {
    padding: 0 !important;
    margin: 0 !important;
  }

  /* ── PAYMENT / REMARKS AREA ── */
  .po-remarks-bar {
    display: grid;
    grid-template-columns: 1fr 1fr;
    border: none;
    margin-bottom: 0;
  }
  .remarks-col {
    padding: 6px 12px;
    font-size: 11px;
    border-right: none;
  }
  .remarks-col:last-child { border-right: none; }

  /* ── DEAR SIR ── */
  .dear-sir {
    padding: 8px 0 4px;
    font-size: 12px;
  }

  /* ── ITEMS TABLE ── */
  .items-tbl {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #cbd5e1;
    font-size: 11px;
    margin-bottom: 0;
  }
  .items-tbl thead tr { background: #f1f5f9; }
  .items-tbl th {
    padding: 7px 6px;
    font-size: 11px;
    font-weight: 700;
    text-align: center;
    border: 1px solid #cbd5e1;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .items-tbl th.left { text-align: left; }
  .items-tbl td {
    padding: 8px 6px;
    border: 1px solid #cbd5e1;
    text-align: center;
    vertical-align: middle;
  }
  .items-tbl td.left { text-align: left; }
  .items-tbl td.right { text-align: right; }
  .item-code { font-size: 10px; color: #666; }
  .item-desc-main { font-weight: 700; font-size: 12px; }
  .item-desc-sub { font-size: 10px; color: #555; }

  /* ── TOTALS ── */
  .totals-section {
    border: none;
    display: grid;
    grid-template-columns: 1fr 280px;
  }
  .amount-words {
    padding: 8px 12px;
    font-size: 11px;
    border-right: none;
    display: flex;
    align-items: center;
  }
  .amount-words b { font-size: 12px; margin-right: 4px; }
  .totals-right { display: flex; flex-direction: column; }
  .t-row {
    display: flex;
    justify-content: space-between;
    padding: 5px 12px;
    border-bottom: 1px solid #e2e8f0;
    font-size: 12px;
  }
  .t-row:last-child { border-bottom: none; }
  .t-row-lbl { font-weight: 700; color: #333; font-size: 11px; }
  .grand-total-bar {
    display: flex;
    justify-content: space-between;
    padding: 8px 12px;
    background: #1a1a1a;
    color: #fff;
    font-weight: 700;
    font-size: 12px;
  }

  /* ── INSTRUCTIONS / T&C ── */
  .instructions-section {
    border: none;
  }
  .instr-row {
    display: flex;
    border-bottom: 1px solid #e2e8f0;
  }
  .instr-row:last-child { border-bottom: none; }
  .instr-lbl {
    font-weight: 700;
    font-size: 11px;
    min-width: 140px;
    padding: 6px 12px;
    border-right: none;
    background: #f8fafc;
    display: flex;
    align-items: flex-start;
  }
  .instr-val {
    padding: 6px 12px;
    font-size: 11px;
    line-height: 1.6;
    flex: 1;
  }
  .tc-section {
    border: none;
    padding: 10px 14px;
    font-size: 11px;
    line-height: 1.7;
  }
  .tc-title {
    font-weight: 700;
    font-size: 12px;
    margin-bottom: 6px;
    text-decoration: underline;
  }

  /* ── SIGNATORY ── */
  .signatory-section {
    border: none;
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-top: 15px;
  }
  .signatory-col {
    padding: 8px 14px 12px;
    border-right: none;
    text-align: center;
    font-size: 11px;
    position: relative;
  }
  .signatory-col:last-child { border-right: none; }
  .signatory-space { height: 80px; display: flex; align-items: center; justify-content: center; position: relative; }
  .signatory-lbl { font-weight: 700; font-size: 12px; }
  .stamp-img { height: 70px; width: auto; object-fit: contain; opacity: 0.92; }
  .sign-img { height: 36px; width: auto; object-fit: contain; opacity: 0.9; position: absolute; bottom: 4px; right: 10px; }

  /* ── FOOTER ── */
  .po-footer {
    margin-top: 12px;
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #666;
  }
  .po-footer-center {
    text-align: center;
    margin-top: 10px;
    font-size: 11px;
    font-weight: 700;
    border: none;
    padding: 7px;
    letter-spacing: 0.06em;
  }

  .status-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
  }

  /* ── HANDOVER FORM STYLES ── */
  .handover-title {
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    margin: 20px 0 10px 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 2px solid #000;
    padding-bottom: 5px;
  }
  .handover-section-title {
    font-size: 11px;
    font-weight: 700;
    background: #f1f5f9;
    padding: 4px 8px;
    border: none;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: 10px;
  }
  .handover-table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #cbd5e1;
    font-size: 10px;
    margin-bottom: 0;
  }
  .handover-table th, .handover-table td {
    border: 1px solid #cbd5e1;
    padding: 4px 6px;
    text-align: left;
    vertical-align: middle;
  }
  .handover-table th {
    background: #f8fafc;
    font-weight: 700;
    text-transform: uppercase;
  }
  .handover-table td.particulars {
    font-weight: 700;
    width: 35%;
    background: #f8fafc;
  }
  .handover-table td.details {
    width: 65%;
  }
  .handover-checkbox-box {
    width: 12px;
    height: 12px;
    border: 1px solid #94a3b8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 9px;
    background: #fff;
    margin-right: 4px;
  }

  .print-spacer {
    display: block;
    flex-grow: 1;
  }

  .handover-block {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    margin-bottom: 12px;
  }

  /* ── PRINT STYLES ── */
  @media print {
    .handover-page-break {
      margin-top: 0 !important;
    }
    .handover-table th, .handover-table td {
      padding: 4px 6px !important;
      font-size: 11px !important;
    }
    .handover-section-title {
      margin-top: 10px !important;
      padding: 4px 8px !important;
      font-size: 12px !important;
    }
    html, body {
      background: white !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body * {
      visibility: hidden;
    }
    .po-wrap, .po-wrap * {
      visibility: visible;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .po-wrap {
      position: absolute;
      left: 0;
      top: 0;
      margin: 0;
      padding: 0;
      background: white;
      width: 100%;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .po-sheet {
      display: flex !important;
      flex-direction: column !important;
      width: 100% !important;
      height: 258mm !important;
      min-height: 258mm !important;
      box-shadow: none !important;
      border: none !important;
      padding: 0 !important;
      margin: 0 !important;
      box-sizing: border-box !important;
      page-break-after: always !important;
      break-after: page !important;
      background: white !important;
    }
    .po-sheet-inner {
      border: 1.5px solid #000 !important;
      flex: 1 !important;
      display: flex !important;
      flex-direction: column !important;
      padding: 16px 24px !important;
      box-sizing: border-box !important;
      height: 100% !important;
    }
    .print-border {
      display: none !important;
    }
    .status-row {
      display: none !important;
    }
    .items-tbl tr, .handover-table tr {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    .totals-section, .signatory-section, .po-footer-center, .po-footer {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    @page {
      margin: 10mm;
      size: A4;
    }
    .print-hide {
      display: none !important;
    }
    .address-block {
      margin-bottom: 0 !important;
      padding: 8px 12px !important;
    }
    .address-block * {
      font-size: 11px !important;
      line-height: 1.5 !important;
    }
    .items-tbl th, .items-tbl td {
      padding: 7px 8px !important;
      font-size: 11px !important;
      line-height: 1.4 !important;
    }
    .totals-section {
      grid-template-columns: 1fr 280px !important;
    }
    .totals-section .amount-words {
      padding: 8px 12px !important;
      font-size: 11px !important;
      line-height: 1.5 !important;
    }
    .totals-section .t-row {
      padding: 6px 12px !important;
      font-size: 12px !important;
    }
    .grand-total-bar {
      padding: 8px 12px !important;
      font-size: 13px !important;
    }
    .signatory-col {
      padding: 8px 12px !important;
    }
    .signatory-space {
      height: 70px !important;
    }
    .stamp-img {
      height: 60px !important;
    }
    .tc-section {
      padding: 8px 12px !important;
      font-size: 10px !important;
      line-height: 1.4 !important;
    }
    .tc-title {
      margin-bottom: 4px !important;
      font-size: 11px !important;
    }
    .po-header-logo {
      padding: 8px 12px !important;
    }
    .po-header-logo img {
      width: 140px !important;
    }
    .po-header-title {
      font-size: 17px !important;
      padding: 8px !important;
    }
    .po-header-meta {
      padding: 0 !important;
    }
    .po-meta-row {
      margin-bottom: 0 !important;
      font-size: 11px !important;
    }
    .dear-sir {
      padding: 6px 0 !important;
      font-size: 12px !important;
    }
  }
`;

const inr = (val: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val);

const amountInWords = (amount: number): string => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const numToWords = (n: number): string => {
        if (n === 0) return '';
        if (n < 20) return ones[n] + ' ';
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '') + ' ';
        if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred ' + numToWords(n % 100);
        if (n < 100000) return numToWords(Math.floor(n / 1000)) + 'Thousand ' + numToWords(n % 1000);
        if (n < 10000000) return numToWords(Math.floor(n / 100000)) + 'Lakh ' + numToWords(n % 100000);
        return numToWords(Math.floor(n / 10000000)) + 'Crore ' + numToWords(n % 10000000);
    };

    const wholePart = Math.floor(amount);
    const decimalPart = Math.round((amount - wholePart) * 100);
    let result = 'INR ' + numToWords(wholePart).trim() + ' Rupees';
    if (decimalPart > 0) result += ' and ' + numToWords(decimalPart).trim() + ' Paise';
    result += ' Only';
    return result;
};

const StatusBlock = () => {
    const record = useRecordContext();
    if (!record || !record.status) return null;
    return <StatusBadge status={record.status} />;
};

// SO-1: pull first values out of a contact's phone_jsonb / email_jsonb arrays.
const jsonbValues = (arr: any, key: string): string =>
    Array.isArray(arr) ? arr.map((x: any) => x?.[key]).filter(Boolean).join(', ') : '';
const ContactPhone = () => {
    const contact = useRecordContext();
    return <>{jsonbValues(contact?.phone_jsonb, 'number') || '-'}</>;
};
const ContactEmail = () => {
    const contact = useRecordContext();
    return <>{jsonbValues(contact?.email_jsonb, 'email') || '-'}</>;
};

const SalesOrderShowActions = ({ showWatermark, onToggleWatermark }: { showWatermark: boolean, onToggleWatermark: () => void }) => {
    const record = useRecordContext();
    return (
        <TopToolbar>
            <Button
                variant={showWatermark ? "secondary" : "outline"}
                size="sm"
                onClick={onToggleWatermark}
                className={showWatermark ? "bg-emerald-600 text-white hover:bg-emerald-700" : "mr-2"}
            >
                {showWatermark ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                Watermark: {showWatermark ? "ON" : "OFF"}
            </Button>
            <EditButton />
            <DocumentPDFButton title="Sales Order" record={record} />
        </TopToolbar>
    );
};

const AmountCell = () => {
    const record = useRecordContext();
    if (!record?.total_amount) return <span className="text-slate-400 text-xs">—</span>;
    return (
        <div className="flex items-center gap-1 font-bold text-[#000]">
            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(record.total_amount)}
        </div>
    );
};

const SalesOrderShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/sales_orders" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back to Sales Orders
                </Link>
            </Button>
            <span className="text-2xl font-bold tracking-tight">Sales Order {record.reference}</span>
        </div>
    );
};

export const SalesOrderShow = () => {
    const [showWatermark, setShowWatermark] = useState(true);

    return (
        <Show 
            title={<SalesOrderShowTitle />}
            actions={<SalesOrderShowActions showWatermark={showWatermark} onToggleWatermark={() => setShowWatermark(!showWatermark)} />}
        >
            <SalesOrderShowInner showWatermark={showWatermark} />
        </Show>
    );
};

const ExternalLinkRender = () => {
    const record = useRecordContext();
    if (!record || !record.file_url) return null;
    return (
        <a href={record.file_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 ml-2">
            <ExternalLink className="w-4 h-4 inline" />
        </a>
    );
};

const hasAnyHandoverData = (data: any): boolean => {
    if (!data || !data._handover) return false;
    const h = data._handover;

    // Helper to check if string is non-empty and not a default value
    const isValFilled = (val: any, defaultVals: string[] = []): boolean => {
        if (typeof val === 'string') {
            const trimmed = val.trim();
            if (trimmed === '') return false;
            return !defaultVals.includes(trimmed);
        }
        if (typeof val === 'boolean') {
            return val === true; // any true boolean is data entered
        }
        return false;
    };

    // 1. Commercial
    const comm = h.commercial || {};
    if (isValFilled(comm.currency, ['INR'])) return true;
    if (isValFilled(comm.unit_price_approval, ['Approved'])) return true;
    if (isValFilled(comm.payment_terms)) return true;
    if (isValFilled(comm.credit_terms)) return true;
    if (isValFilled(comm.taxes_duties, ['GST @ 18%'])) return true;
    if (isValFilled(comm.incoterms_freight)) return true;
    if (isValFilled(comm.special_conditions)) return true;

    // 2. Technical
    const tech = h.technical || {};
    if (isValFilled(tech.part_number)) return true;
    if (isValFilled(tech.part_description)) return true;
    if (isValFilled(tech.drawing_number)) return true;
    if (isValFilled(tech.drawing_revision)) return true;
    if (isValFilled(tech.specification_standard)) return true;
    if (isValFilled(tech.scope_of_supply)) return true;
    if (isValFilled(tech.scope_exclusions)) return true;
    if (isValFilled(tech.customer_critical_characteristics)) return true;
    if (isValFilled(tech.special_process_requirements)) return true;
    
    // Technical docs attached
    const docs = tech.docs_attached || {};
    if (Object.values(docs).some(val => val === true)) return true;

    // 3. Execution
    const exec = h.execution || {};
    if (isValFilled(exec.batch_size)) return true;
    if (isValFilled(exec.production_lead_time)) return true;
    if (isValFilled(exec.delivery_commitment)) return true;
    if (isValFilled(exec.priority_level, ['Normal'])) return true;
    if (isValFilled(exec.capacity_availability_confirmed_by)) return true;
    if (isValFilled(exec.tooling_fixture_availability, ['Available'])) return true;
    if (isValFilled(exec.gauge_requirement)) return true;
    if (isValFilled(exec.special_equipment_requirement)) return true;

    // Delivery schedule
    const scheds = exec.delivery_schedule || [];
    for (const s of scheds) {
        if (isValFilled(s.qty) || isValFilled(s.delivery_date) || isValFilled(s.remarks)) return true;
    }

    // 4. Procurement
    const proc = h.procurement || {};
    if (isValFilled(proc.raw_material_specification)) return true;
    if (isValFilled(proc.approved_source_make)) return true;
    if (isValFilled(proc.long_lead_items, ['No'])) return true;
    if (isValFilled(proc.procurement_lead_time)) return true;
    if (isValFilled(proc.customer_free_issue_material, ['No'])) return true;
    if (isValFilled(proc.free_issue_material_details)) return true;

    // 5. Quality
    const qual = h.quality || {};
    const qDocs = qual.docs_required || {};
    if (Object.values(qDocs).some(val => val === true || (typeof val === 'string' && val.trim() !== ''))) return true;
    if (isValFilled(qual.acceptance_criteria)) return true;

    // 6. Logistics
    const log = h.logistics || {};
    if (isValFilled(log.packaging_specification)) return true;
    if (isValFilled(log.labelling_requirement)) return true;
    if (isValFilled(log.dispatch_location)) return true;
    if (isValFilled(log.transportation_responsibility, ['Company'])) return true;
    if (isValFilled(log.mode_of_transport, ['Road'])) return true;
    if (isValFilled(log.special_handling_instructions)) return true;

    // 7. Risks
    const risks = h.risks || [];
    for (const r of risks) {
        if (isValFilled(r.description) || isValFilled(r.owner) || isValFilled(r.action)) return true;
    }

    // 8. Deviations
    if (isValFilled(h.customer_deviations)) return true;

    // 9. Confirmations
    const conf = h.confirmation || {};
    for (const dept of Object.keys(conf)) {
        const c = conf[dept] || {};
        if (isValFilled(c.name) || c.signed === true || isValFilled(c.date)) return true;
    }

    // 10. Release Auth
    const release = h.release_auth || {};
    const salesAuth = release.sales || {};
    if (isValFilled(salesAuth.name) || salesAuth.signed === true || isValFilled(salesAuth.date)) return true;
    const opsAuth = release.operations || {};
    if (isValFilled(opsAuth.name) || opsAuth.signed === true || isValFilled(opsAuth.date)) return true;

    return false;
};

const SalesOrderShowInner = ({ showWatermark }: { showWatermark: boolean }) => {
    const record = useRecordContext();
    const config = useConfigurationContext();
    const { data: linkedPOs } = useGetList(
        'purchase_orders',
        {
            filter: { sales_order_id: record?.id },
            pagination: { page: 1, perPage: 50 }
        },
        { enabled: !!record?.id }
    );

    // Read handover details from localStorage
    const handoverDataStr = record?.id ? localStorage.getItem(`gsw_so_handover_${record.id}`) : null;

    let handoverData: any = null;
    if (handoverDataStr) {
        try {
            handoverData = JSON.parse(handoverDataStr);
        } catch (e) {
            console.error("Failed to parse handover data", e);
        }
    }

    if (!record) return null;

    const taxAmount = record.tax_amount !== undefined 
        ? Number(record.tax_amount) 
        : (Number(record.cgst_amount || 0) + Number(record.sgst_amount || 0) + Number(record.igst_amount || 0));

    return (
        <>
            <style>{css}</style>
            <div className="po-wrap">
                <div className="po-sheet">
                    <div className="po-sheet-inner">
                        <WatermarkOverlay isVisible={showWatermark} />

                        {/* ── HEADER BAR ── */}
                        <div className="po-header-bar">
                            <div className="po-header-logo">
                                <img src="/logos/rovasys_horizontal_clean.png" alt="Rovasys" />
                                <div style={{ fontSize: 9, color: '#555', marginTop: 4, lineHeight: 1.4 }}>
                                    W-145, S-Block, MIDC-Bhosari, Pune - 411026<br />
                                    Ph: +91 91722 01844 | info@rovasys.com
                                </div>
                            </div>
                            <div className="po-header-title">SALES ORDER</div>
                            <div className="po-header-meta">
                                <div className="po-meta-row">
                                    <span className="po-meta-lbl">SO NO</span>
                                    <span className="po-meta-val" style={{ whiteSpace: 'nowrap' }}>{record.reference}</span>
                                </div>
                                <div className="po-meta-row">
                                    <span className="po-meta-lbl">Date</span>
                                    <span className="po-meta-val"><DateField source="date" /></span>
                                </div>
                                {record.customer_po_no && (
                                    <div className="po-meta-row">
                                        <span className="po-meta-lbl">PO No</span>
                                        <span className="po-meta-val">{record.customer_po_no}</span>
                                    </div>
                                )}
                                {record.customer_po_date && (
                                    <div className="po-meta-row">
                                        <span className="po-meta-lbl">PO Date</span>
                                        <span className="po-meta-val"><DateField source="customer_po_date" /></span>
                                    </div>
                                )}
                                {record.payment_term_days && (
                                    <div className="po-meta-row">
                                        <span className="po-meta-lbl">Pay Terms</span>
                                        <span className="po-meta-val">{record.payment_term_days} days</span>
                                    </div>
                                )}
                                {record.valid_until && (
                                    <div className="po-meta-row">
                                        <span className="po-meta-lbl">Valid Until</span>
                                        <span className="po-meta-val"><DateField source="valid_until" /></span>
                                    </div>
                                )}
                                {record.quotation_id && (
                                    <div className="po-meta-row">
                                        <span className="po-meta-lbl">Ref Quote</span>
                                        <span className="po-meta-val">
                                            <ReferenceField source="quotation_id" reference="quotations" link={false}>
                                                <TextField source="reference" />
                                            </ReferenceField>
                                        </span>
                                    </div>
                                )}
                                {record.project_id && (
                                    <div className="po-meta-row">
                                        <span className="po-meta-lbl">Project</span>
                                        <span className="po-meta-val">
                                            <ReferenceField source="project_id" reference="projects" link={false}>
                                                <TextField source="name" />
                                            </ReferenceField>
                                        </span>
                                    </div>
                                )}
                                <div className="po-meta-row">
                                    <span className="po-meta-lbl">Status</span>
                                    <span className="po-meta-val" style={{ textTransform: 'uppercase' }}>{record.status}</span>
                                </div>
                            </div>
                        </div>

                        {/* ── FROM / TO ── */}
                        <div className="so-from-to-grid">
                            <AddressBlock label="Supplier Details" addressOverride={
                                <>
                                    <strong>ROVASYS ALLIED WORKS PVT. LTD.</strong><br/>
                                    W-145, S-Block, MIDC-Bhosari,<br />
                                    Pune, Maharashtra – 411026<br />
                                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}><span style={{ fontWeight: 700, minWidth: 90 }}>Ph</span><span>: +91 91722 01844</span></div>
                                    <div style={{ display: 'flex', gap: 4 }}><span style={{ fontWeight: 700, minWidth: 90 }}>E-mail</span><span>: info@rovasys.com</span></div>
                                    <div style={{ display: 'flex', gap: 4 }}><span style={{ fontWeight: 700, minWidth: 90 }}>GSTIN</span><span>: 27AAMCR6049R1ZD</span></div>
                                    <div style={{ display: 'flex', gap: 4 }}><span style={{ fontWeight: 700, minWidth: 90 }}>PAN</span><span>: AAMCR6049R</span></div>
                                    <div style={{ display: 'flex', gap: 4 }}><span style={{ fontWeight: 700, minWidth: 90 }}>State Code</span><span>: 27</span></div>
                                </>
                            } />

                            <AddressBlock label="Bill To" companyId={record.company_id}>
                                <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #ddd' }}>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <span style={{ fontWeight: 700, minWidth: 90 }}>Contact Person:</span>
                                        <span>
                                            <ReferenceField source="contact_id" reference="contacts" link={false}>
                                                <TextField source="first_name" />{' '}<TextField source="last_name" />
                                            </ReferenceField>
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <span style={{ fontWeight: 700, minWidth: 90 }}>Contact No:</span>
                                        <span>
                                            <ReferenceField source="contact_id" reference="contacts" link={false}>
                                                <ContactPhone />
                                            </ReferenceField>
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <span style={{ fontWeight: 700, minWidth: 90 }}>Email ID:</span>
                                        <span>
                                            <ReferenceField source="contact_id" reference="contacts" link={false}>
                                                <ContactEmail />
                                            </ReferenceField>
                                        </span>
                                    </div>
                                </div>
                            </AddressBlock>
                        </div>

                        {/* ── SHIP TO ── */}
                        <div className="so-ship-to-bar">
                            <AddressBlock 
                                companyId={record.company_id} 
                                useShipping 
                                label="Ship To" 
                            />
                        </div>

                        {/* ── DEAR SIR ── */}
                        <div className="dear-sir">
                            Dear Sir,<br />
                            <span style={{ marginLeft: 20 }}>We are pleased to accept and confirm your order as per the terms and conditions outlined below.</span>
                        </div>

                        {/* ── ITEMS TABLE ── */}
                        <table className="items-tbl">
                            <thead>
                                <tr>
                                    <th style={{ width: 44, whiteSpace: 'nowrap' }}>S.No.</th>
                                    <th className="left">Description</th>
                                    <th style={{ width: 80 }}>HSN / SAC</th>
                                    <th style={{ width: 56 }}>GST %</th>
                                    <th style={{ width: 68 }}>QTY</th>
                                    <th style={{ width: 52 }}>UOM</th>
                                    <th style={{ width: 100 }}>Rate</th>
                                    <th style={{ width: 110 }}>Total Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {record.items?.map((item: any, index: number) => (
                                    <tr key={index}>
                                        <td>{String(index + 1).padStart(5, '0')}</td>
                                        <td className="left">
                                            <div className="item-desc-main">{item.description}</div>
                                        </td>
                                        <td>{item.hsn_code || '-'}</td>
                                        <td>{item.tax_rate ?? 18}%</td>
                                        <td style={{ fontWeight: 700 }}>{Intl.NumberFormat('en-IN').format(item.quantity)}</td>
                                        <td>{item.unit || 'Nos'}</td>
                                        <td className="right">{Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(item.unit_price)}</td>
                                        <td className="right" style={{ fontWeight: 700 }}>{Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(item.line_total)}</td>
                                    </tr>
                                ))}
                                {/* padding row */}
                                <tr className="print-hide">
                                    <td colSpan={8} style={{ height: 40, borderBottom: 'none' }} />
                                </tr>
                            </tbody>
                        </table>

                        {/* ── TOTALS ── */}
                        <div className="totals-section">
                            <div className="amount-words">
                                <div>
                                    <b>Amount In Words:</b>
                                    <span style={{ color: '#222' }}> {amountInWords(record.total_amount)}</span>
                                </div>
                            </div>
                            <div className="totals-right">
                                <div className="t-row">
                                    <span className="t-row-lbl">Sub Total</span>
                                    <span style={{ fontWeight: 700 }}>{inr(record.subtotal)}</span>
                                </div>
                                <div className="t-row">
                                    <span className="t-row-lbl">GST @ 18.00%</span>
                                    <span style={{ fontWeight: 700 }}>{inr(taxAmount)}</span>
                                </div>
                                <div className="grand-total-bar">
                                    <span style={{ letterSpacing: '0.08em' }}>GRAND TOTAL</span>
                                    <span style={{ fontSize: 14 }}>{inr(record.total_amount)}</span>
                                </div>
                            </div>
                        </div>

                        {/* ── TERMS & CONDITIONS ── */}
                        {record.terms_conditions && (
                            <div className="tc-section">
                                <div className="tc-title">Terms &amp; Conditions :</div>
                                <TextField source="terms_conditions" />
                            </div>
                        )}

                        <div className="print-spacer" />

                        <div className="footer-group">
                            {/* ── LINKED PURCHASE ORDERS ── */}
                            {linkedPOs && linkedPOs.length > 0 && (
                                <div style={{ margin: '12px 0' }}>
                                    <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', textDecoration: 'underline', marginBottom: 6 }}>Linked Purchase Orders</div>
                                    <table className="items-tbl">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 120 }}>PO No.</th>
                                                <th style={{ width: 100 }}>Date</th>
                                                <th className="left">Vendor</th>
                                                <th style={{ width: 120 }}>Amount</th>
                                                <th style={{ width: 100 }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {linkedPOs.map((po: any, idx: number) => (
                                                <tr key={idx}>
                                                    <td style={{ fontWeight: 700 }}>
                                                        <Link to={`/purchase_orders/${po.id}/show`} className="text-black hover:underline font-bold print:no-underline">
                                                            {po.reference}
                                                        </Link>
                                                    </td>
                                                    <td>{po.date ? new Date(po.date).toLocaleDateString('en-IN') : '-'}</td>
                                                    <td className="left">
                                                        <ReferenceField record={po} source="vendor_id" reference="vendors" link={false}>
                                                            <TextField source="name" />
                                                        </ReferenceField>
                                                    </td>
                                                    <td className="right" style={{ fontWeight: 700 }}>{inr(po.total_amount)}</td>
                                                    <td style={{ textTransform: 'uppercase' }}>{po.status}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="po-footer">
                                <span>Page: 1 / {hasAnyHandoverData(handoverData) ? '4' : '1'}</span>
                                {config.showPrintTimestamp && (
                                    <span>Print Date/Time: {new Date().toLocaleDateString('en-IN')} / {new Date().toLocaleTimeString('en-IN')}</span>
                                )}
                            </div>

                        </div>
                    </div>
                </div>

                {/* Handover Form Tables */}
                {hasAnyHandoverData(handoverData) && (
                    <>
                        {/* Page 2: Handover Form Part 1 */}
                        <div className="po-sheet handover-page-break">
                            <div className="po-sheet-inner">
                                <WatermarkOverlay isVisible={showWatermark} />
                                <HandoverFormRenderPart1 record={record} handoverData={handoverData} />
                                <div className="print-spacer" />
                                <div className="po-footer">
                                    <span>Page: 2 / 4</span>
                                    {config.showPrintTimestamp && (
                                        <span>Print Date/Time: {new Date().toLocaleDateString('en-IN')} / {new Date().toLocaleTimeString('en-IN')}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Page 3: Handover Form Part 2 */}
                        <div className="po-sheet handover-page-break">
                            <div className="po-sheet-inner">
                                <WatermarkOverlay isVisible={showWatermark} />
                                <HandoverFormRenderPart2 record={record} handoverData={handoverData} />
                                <div className="print-spacer" />
                                <div className="po-footer">
                                    <span>Page: 3 / 4</span>
                                    {config.showPrintTimestamp && (
                                        <span>Print Date/Time: {new Date().toLocaleDateString('en-IN')} / {new Date().toLocaleTimeString('en-IN')}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Page 4: Handover Form Part 3 */}
                        <div className="po-sheet handover-page-break">
                            <div className="po-sheet-inner">
                                <WatermarkOverlay isVisible={showWatermark} />
                                <HandoverFormRenderPart3 record={record} handoverData={handoverData} />
                                <div className="print-spacer" />
                                <div className="po-footer">
                                    <span>Page: 4 / 4</span>
                                    {config.showPrintTimestamp && (
                                        <span>Print Date/Time: {new Date().toLocaleDateString('en-IN')} / {new Date().toLocaleTimeString('en-IN')}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ── ADDITIONAL DETAILS (print-hide / status-block-print) ── */}
                <div className="print-hide status-block-print max-w-[960px] mx-auto mt-6 space-y-6">
                    {/* Drawing / Document Link */}
                    {record.file_url && (
                        <Card className="shadow-sm">
                            <CardHeader className="pb-3 border-b bg-slate-50/50">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-primary" />
                                        <CardTitle className="text-lg font-semibold tracking-tight">Drawing / Document Link</CardTitle>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-2">
                                    <TextField source="file_url" />
                                    <ExternalLinkRender />
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Internal Comments / Notes */}
                    {record.comments && (
                        <Card className="shadow-sm">
                            <CardHeader className="pb-3 border-b bg-slate-50/50">
                                <div className="flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-primary" />
                                    <CardTitle className="text-lg font-semibold tracking-tight">Internal Comments / Notes</CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <div className="bg-slate-50 p-4 rounded-md border text-sm whitespace-pre-wrap">
                                    <TextField source="comments" />
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </>
    );
};

const HandoverFormRenderPart1 = ({ record, handoverData }: { record: any, handoverData: any }) => {
    const hVal = (path: string) => {
        if (!handoverData?._handover) return '';
        const parts = path.split('.');
        let current = handoverData._handover;
        for (const part of parts) {
            if (current == null) return '';
            current = current[part];
        }
        return current ?? '';
    };

    return (
        <div className="handover-block">
            {/* Header */}
            <div style={{ textAlign: 'center', borderBottom: '2.5px solid #000', paddingBottom: '8px', marginBottom: '15px' }}>
                <div style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Sales Order Release & Project Handover Form
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '1.5px solid #000', padding: '8px 0', fontSize: '11px', fontWeight: 700, marginBottom: '10px' }}>
                <div>Doc No: {hVal('header.doc_no') || 'RVS-SO-000'}</div>
                <div style={{ textAlign: 'center' }}>Date: {hVal('header.date') ? new Date(hVal('header.date')).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}</div>
                <div style={{ textAlign: 'right' }}>Rev No: {hVal('header.rev_no') || '00'}</div>
            </div>

            <div style={{ fontSize: '11px', fontStyle: 'italic', margin: '0 0 15px 0', padding: '6px 0', color: '#555' }}>
                <strong>Purpose:</strong> To ensure complete cross-functional alignment and availability of all customer, commercial, technical, quality, planning, procurement, and logistics information prior to commencement of execution.
            </div>

            {/* A. Sales Order Details */}
            <div className="handover-section-title">A. Sales Order Details</div>
            <table className="handover-table">
                <tbody>
                    <tr>
                        <td className="particulars">Sales Order No.</td>
                        <td className="details">{record.reference}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Sales Order Date</td>
                        <td className="details">{record.date ? new Date(record.date).toLocaleDateString('en-IN') : '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Customer Name</td>
                        <td className="details">
                            <ReferenceField source="company_id" reference="companies" link={false}>
                                <TextField source="name" />
                            </ReferenceField>
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Customer Code</td>
                        <td className="details">
                            <ReferenceField source="company_id" reference="companies" link={false}>
                                <TextField source="code" />
                            </ReferenceField>
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Customer PO No.</td>
                        <td className="details">{record.customer_po_no || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Customer PO Date</td>
                        <td className="details">{record.customer_po_date ? new Date(record.customer_po_date).toLocaleDateString('en-IN') : '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Project / Program Name</td>
                        <td className="details">
                            <ReferenceField source="project_id" reference="projects" link={false}>
                                <TextField source="name" />
                            </ReferenceField>
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Customer Contact Person</td>
                        <td className="details">
                            <ReferenceField source="contact_id" reference="contacts" link={false}>
                                <div className="flex gap-1">
                                    <TextField source="first_name" /> <TextField source="last_name" />
                                </div>
                            </ReferenceField>
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Contact No. / Email</td>
                        <td className="details">
                            <ReferenceField source="contact_id" reference="contacts" link={false}>
                                <div className="flex flex-col gap-0.5">
                                    <div>Phone: <ContactPhone /></div>
                                    <div>Email: <ContactEmail /></div>
                                </div>
                            </ReferenceField>
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Billing Address</td>
                        <td className="details" style={{ whiteSpace: 'pre-wrap' }}>{record.billing_address || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Shipping Address</td>
                        <td className="details" style={{ whiteSpace: 'pre-wrap' }}>{record.shipping_address || '-'}</td>
                    </tr>
                </tbody>
            </table>

            {/* B. Commercial Details */}
            <div className="handover-section-title">B. Commercial Details</div>
            <table className="handover-table">
                <tbody>
                    <tr>
                        <td className="particulars">Total Order Value</td>
                        <td className="details" style={{ fontWeight: 700 }}>
                            {inr(record.total_amount)}
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Currency</td>
                        <td className="details">{hVal('commercial.currency') || 'INR'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Unit Price Approval</td>
                        <td className="details">
                            <div className="flex gap-6">
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('commercial.unit_price_approval') === 'Approved' ? '✓' : ''}</span> Approved
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('commercial.unit_price_approval') === 'Exception Approved' ? '✓' : ''}</span> Exception Approved
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Payment Terms</td>
                        <td className="details">{hVal('commercial.payment_terms') || (record.payment_term_days ? `${record.payment_term_days} days` : '-')}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Credit Terms</td>
                        <td className="details">{hVal('commercial.credit_terms') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Taxes & Duties</td>
                        <td className="details">{hVal('commercial.taxes_duties') || 'GST @ 18%'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Incoterms / Freight Terms</td>
                        <td className="details">{hVal('commercial.incoterms_freight') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Special Commercial Conditions</td>
                        <td className="details" style={{ whiteSpace: 'pre-wrap' }}>{hVal('commercial.special_conditions') || '-'}</td>
                    </tr>
                </tbody>
            </table>

            {/* C. Product & Technical Info */}
            <div className="handover-section-title">C. Product & Technical Information</div>
            <table className="handover-table">
                <tbody>
                    <tr>
                        <td className="particulars">Part Number</td>
                        <td className="details">{hVal('technical.part_number') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Part Description</td>
                        <td className="details">{hVal('technical.part_description') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Drawing Number</td>
                        <td className="details">{hVal('technical.drawing_number') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Drawing Revision</td>
                        <td className="details">{hVal('technical.drawing_revision') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Specification / Standard</td>
                        <td className="details">{hVal('technical.specification_standard') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Scope of Supply</td>
                        <td className="details">{hVal('technical.scope_of_supply') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Scope Exclusions</td>
                        <td className="details">{hVal('technical.scope_exclusions') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Customer Critical Characteristics</td>
                        <td className="details">{hVal('technical.customer_critical_characteristics') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Special Process Requirements</td>
                        <td className="details">{hVal('technical.special_process_requirements') || '-'}</td>
                    </tr>
                </tbody>
            </table>

            <div style={{ fontStyle: 'normal', fontSize: '10px', padding: '6px 0', background: 'transparent' }}>
                <strong style={{ fontSize: '10px' }}>Documents Attached:</strong>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px', marginTop: '6px' }}>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('technical.docs_attached.approved_drawings') ? '✓' : ''}</span> Approved Drawings
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('technical.docs_attached.technical_specifications') ? '✓' : ''}</span> Technical Specifications
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('technical.docs_attached.bom_applicable') ? '✓' : ''}</span> BOM (if applicable)
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('technical.docs_attached.customer_standards') ? '✓' : ''}</span> Customer Standards
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('technical.docs_attached.process_requirements') ? '✓' : ''}</span> Process Requirements
                    </span>
                </div>
            </div>
        </div>
    );
};

const HandoverFormRenderPart2 = ({ record, handoverData }: { record: any, handoverData: any }) => {
    const hVal = (path: string) => {
        if (!handoverData?._handover) return '';
        const parts = path.split('.');
        let current = handoverData._handover;
        for (const part of parts) {
            if (current == null) return '';
            current = current[part];
        }
        return current ?? '';
    };

    return (
        <div className="handover-block">
            {/* D. Order Execution Plan */}
            <div className="handover-section-title">D. Order Execution Plan</div>
            <table className="handover-table">
                <tbody>
                    <tr>
                        <td className="particulars">Total Order Quantity</td>
                        <td className="details">{hVal('execution.total_quantity') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Batch / Lot Size</td>
                        <td className="details">{hVal('execution.batch_size') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Production Lead Time</td>
                        <td className="details">{hVal('execution.production_lead_time') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Delivery Commitment</td>
                        <td className="details">{hVal('execution.delivery_commitment') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Priority Level</td>
                        <td className="details">
                            <div className="flex gap-6">
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('execution.priority_level') === 'Normal' ? '✓' : ''}</span> Normal
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('execution.priority_level') === 'Urgent' ? '✓' : ''}</span> Urgent
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('execution.priority_level') === 'Critical' ? '✓' : ''}</span> Critical
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Capacity Availability Confirmed By</td>
                        <td className="details">{hVal('execution.capacity_availability_confirmed_by') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Tooling / Fixture Availability</td>
                        <td className="details">
                            <div className="flex gap-6">
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('execution.tooling_fixture_availability') === 'Available' ? '✓' : ''}</span> Available
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('execution.tooling_fixture_availability') === 'To be Arranged' ? '✓' : ''}</span> To be Arranged
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Gauge Requirement</td>
                        <td className="details">{hVal('execution.gauge_requirement') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Special Equipment Requirement</td>
                        <td className="details">{hVal('execution.special_equipment_requirement') || '-'}</td>
                    </tr>
                </tbody>
            </table>

            {/* E. Delivery Schedule */}
            <div className="handover-section-title">E. Delivery Schedule</div>
            <table className="handover-table">
                <thead>
                    <tr>
                        <th style={{ width: 60, textAlign: 'center' }}>Sr. No.</th>
                        <th>Quantity</th>
                        <th>Required Delivery Date</th>
                        <th>Remarks</th>
                    </tr>
                </thead>
                <tbody>
                    {([0, 1, 2, 3] as number[]).map((idx) => {
                        const row = hVal(`execution.delivery_schedule.${idx}`) as any;
                        return (
                            <tr key={idx}>
                                <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                <td>{row?.qty || '-'}</td>
                                <td>{row?.delivery_date ? new Date(row.delivery_date).toLocaleDateString('en-IN') : '-'}</td>
                                <td>{row?.remarks || '-'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* F. Procurement Requirements */}
            <div className="handover-section-title">F. Procurement Requirements</div>
            <table className="handover-table">
                <tbody>
                    <tr>
                        <td className="particulars">Raw Material Specification</td>
                        <td className="details">{hVal('procurement.raw_material_specification') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Approved Source / Make</td>
                        <td className="details">{hVal('procurement.approved_source_make') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Long Lead Items</td>
                        <td className="details">
                            <div className="flex gap-6">
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('procurement.long_lead_items') === 'Yes' ? '✓' : ''}</span> Yes
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('procurement.long_lead_items') === 'No' ? '✓' : ''}</span> No
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Procurement Lead Time</td>
                        <td className="details">{hVal('procurement.procurement_lead_time') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Customer Free-Issue Material</td>
                        <td className="details">
                            <div className="flex gap-6">
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('procurement.customer_free_issue_material') === 'Yes' ? '✓' : ''}</span> Yes
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('procurement.customer_free_issue_material') === 'No' ? '✓' : ''}</span> No
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Free-Issue Material Details</td>
                        <td className="details" style={{ whiteSpace: 'pre-wrap' }}>{hVal('procurement.free_issue_material_details') || '-'}</td>
                    </tr>
                </tbody>
            </table>

            {/* G. Quality Requirements */}
            <div className="handover-section-title">G. Quality Requirements</div>
            <div style={{ fontSize: '10px', padding: '6px 0', background: 'transparent' }}>
                <strong>Customer Documentation Requirements:</strong>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', marginTop: '6px' }}>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('quality.docs_required.mtc') ? '✓' : ''}</span> Material Test Certificate (MTC)
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('quality.docs_required.dimensional_inspection') ? '✓' : ''}</span> Dimensional Inspection Report
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('quality.docs_required.cmm') ? '✓' : ''}</span> CMM Report
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('quality.docs_required.fai') ? '✓' : ''}</span> First Article Inspection (FAI)
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('quality.docs_required.ppap') ? '✓' : ''}</span> PPAP Submission
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('quality.docs_required.salt_spray') ? '✓' : ''}</span> Salt Spray Test Report
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('quality.docs_required.coating_inspection') ? '✓' : ''}</span> Coating Inspection Report
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('quality.docs_required.certificate_conformance') ? '✓' : ''}</span> Certificate of Conformance
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('quality.docs_required.packing_list') ? '✓' : ''}</span> Packing List
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="handover-checkbox-box">{hVal('quality.docs_required.dispatch_documents') ? '✓' : ''}</span> Dispatch Documents
                    </span>
                    <span className="flex items-center gap-2 col-span-2">
                        <span className="handover-checkbox-box">{hVal('quality.docs_required.other') ? '✓' : ''}</span> Other: <span style={{ textDecoration: 'underline' }}>{hVal('quality.docs_required.other_details') || '__________________'}</span>
                    </span>
                </div>
            </div>
            <table className="handover-table">
                <tbody>
                    <tr>
                        <td className="particulars">Quality Requirements / Acceptance Criteria</td>
                        <td className="details" style={{ whiteSpace: 'pre-wrap' }}>{hVal('quality.acceptance_criteria') || '-'}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

const HandoverFormRenderPart3 = ({ record, handoverData }: { record: any, handoverData: any }) => {
    const hVal = (path: string) => {
        if (!handoverData?._handover) return '';
        const parts = path.split('.');
        let current = handoverData._handover;
        for (const part of parts) {
            if (current == null) return '';
            current = current[part];
        }
        return current ?? '';
    };

    return (
        <div className="handover-block">
            {/* H. Packaging & Logistics */}
            <div className="handover-section-title">H. Packaging & Logistics Requirements</div>
            <table className="handover-table">
                <tbody>
                    <tr>
                        <td className="particulars">Packaging Specification</td>
                        <td className="details">{hVal('logistics.packaging_specification') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Labelling Requirement</td>
                        <td className="details">{hVal('logistics.labelling_requirement') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Dispatch Location</td>
                        <td className="details">{hVal('logistics.dispatch_location') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Consignee Details</td>
                        <td className="details" style={{ whiteSpace: 'pre-wrap' }}>{hVal('logistics.consignee_details') || '-'}</td>
                    </tr>
                    <tr>
                        <td className="particulars">Transportation Responsibility</td>
                        <td className="details">
                            <div className="flex gap-6">
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('logistics.transportation_responsibility') === 'Customer' ? '✓' : ''}</span> Customer
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('logistics.transportation_responsibility') === 'Company' ? '✓' : ''}</span> Company
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('logistics.transportation_responsibility') === 'Third Party' ? '✓' : ''}</span> Third Party
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Mode of Transport</td>
                        <td className="details">
                            <div className="flex gap-6">
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('logistics.mode_of_transport') === 'Road' ? '✓' : ''}</span> Road
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('logistics.mode_of_transport') === 'Air' ? '✓' : ''}</span> Air
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('logistics.mode_of_transport') === 'Sea' ? '✓' : ''}</span> Sea
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="handover-checkbox-box">{hVal('logistics.mode_of_transport') === 'Courier' ? '✓' : ''}</span> Courier
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td className="particulars">Special Handling Instructions</td>
                        <td className="details" style={{ whiteSpace: 'pre-wrap' }}>{hVal('logistics.special_handling_instructions') || '-'}</td>
                    </tr>
                </tbody>
            </table>

            {/* I. Risks, Assumptions & Dependencies */}
            <div className="handover-section-title">I. Risks, Assumptions & Dependencies</div>
            <table className="handover-table">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th style={{ width: 150 }}>Owner</th>
                        <th style={{ width: 250 }}>Action Required</th>
                    </tr>
                </thead>
                <tbody>
                    {([0, 1, 2] as number[]).map((idx) => {
                        const risk = hVal(`risks.${idx}`) as any;
                        return (
                            <tr key={idx}>
                                <td>{risk?.description || '-'}</td>
                                <td>{risk?.owner || '-'}</td>
                                <td>{risk?.action || '-'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* J. Customer Deviations */}
            <div className="handover-section-title">J. Customer Deviations / Special Approvals</div>
            <table className="handover-table">
                <tbody>
                    <tr>
                        <td className="details" style={{ whiteSpace: 'pre-wrap', padding: '10px' }}>
                            {hVal('customer_deviations') || '-'}
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* K. Cross-Functional Handover */}
            <div className="handover-section-title">K. Cross-Functional Handover & Confirmation</div>
            <div style={{ fontSize: '10px', fontStyle: 'italic', padding: '6px 0', background: 'transparent' }}>
                "The undersigned confirm that the information provided above has been reviewed and is adequate for execution of the order in line with customer requirements."
            </div>
            <table className="handover-table">
                <thead>
                    <tr>
                        <th>Department</th>
                        <th>Name</th>
                        <th style={{ width: 120, textAlign: 'center' }}>Signature</th>
                        <th style={{ width: 120 }}>Date</th>
                    </tr>
                </thead>
                <tbody>
                    {['sales', 'operations', 'planning', 'procurement', 'quality', 'logistics', 'finance'].map((dept) => {
                        const c = hVal(`confirmation.${dept}`) as any;
                        return (
                            <tr key={dept}>
                                <td className="particulars capitalize" style={{ width: '25%', background: 'none' }}>
                                    {dept === 'finance' ? 'Finance (if applicable)' : dept}
                                </td>
                                <td>{c?.name || '-'}</td>
                                <td style={{ textAlign: 'center' }}>
                                    {c?.signed ? <span style={{ color: '#000', fontWeight: 'bold' }}>Signed ✓</span> : <span style={{ color: '#888' }}>Pending</span>}
                                </td>
                                <td>{c?.date ? new Date(c.date).toLocaleDateString('en-IN') : '-'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Release Authorization */}
            <div className="handover-section-title">Release Authorization</div>
            <table className="handover-table">
                <thead>
                    <tr>
                        <th style={{ width: '50%' }}>Released By (Sales)</th>
                        <th style={{ width: '50%' }}>Accepted for Execution (Operations)</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={{ verticalAlign: 'top', padding: '6px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '4px', lineHeight: '1.4' }}>
                                <span style={{ fontWeight: 700 }}>Name</span>
                                <span>: {hVal('release_auth.sales.name') || '____________________'}</span>
                                <span style={{ fontWeight: 700 }}>Designation</span>
                                <span>: {hVal('release_auth.sales.designation') || 'Sales'}</span>
                                <span style={{ fontWeight: 700 }}>Signature</span>
                                <span>: {hVal('release_auth.sales.signed') ? <span style={{ color: '#000', fontWeight: 'bold' }}>Approved ✓</span> : '____________________'}</span>
                                <span style={{ fontWeight: 700 }}>Date</span>
                                <span>: {hVal('release_auth.sales.date') ? new Date(hVal('release_auth.sales.date')).toLocaleDateString('en-IN') : '____________________'}</span>
                            </div>
                        </td>
                        <td style={{ verticalAlign: 'top', padding: '6px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '4px', lineHeight: '1.4' }}>
                                <span style={{ fontWeight: 700 }}>Name</span>
                                <span>: {hVal('release_auth.operations.name') || '____________________'}</span>
                                <span style={{ fontWeight: 700 }}>Designation</span>
                                <span>: {hVal('release_auth.operations.designation') || 'Operations'}</span>
                                <span style={{ fontWeight: 700 }}>Signature</span>
                                <span>: {hVal('release_auth.operations.signed') ? <span style={{ color: '#000', fontWeight: 'bold' }}>Approved ✓</span> : '____________________'}</span>
                                <span style={{ fontWeight: 700 }}>Date</span>
                                <span>: {hVal('release_auth.operations.date') ? new Date(hVal('release_auth.operations.date')).toLocaleDateString('en-IN') : '____________________'}</span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
